<template>
   <div
      class="nav-item"
      :class="{ 'suppress-hover': localSuppressHover }"
      @mouseleave="localSuppressHover = false"
   >
      <a target="_top" class="nav-item__link" itemprop="url" :href="itemLink" @click="onClick">
         {{ itemName
         }}<svg
            v-if="isDropdown"
            class="dropdown-icon"
            viewBox="0 0 10 6"
            fill="none"
            aria-hidden="true"
         >
            <path
               d="M1 1l4 4 4-4"
               stroke="currentColor"
               stroke-width="1.9"
               stroke-linecap="round"
            /></svg
      ></a>
      <div
         v-if="simpleDropdown || sidebysideDropdown || stylishDropdown"
         class="drop-down"
         @click="localSuppressHover = true"
      >
         <simpleDropdown
            v-if="simpleDropdown"
            :dropdown-items="dropdownItems"
            :item-name="itemName"
         />
         <SideBySide
            v-if="sidebysideDropdown"
            :sidebyside-dropdown="sidebysideDropdown"
            :dropdown-items="dropdownItems"
         />
         <StylishDropdown
            v-if="stylishDropdown"
            :stylish-dropdown="stylishDropdown"
            :dropdown-items="dropdownItems"
         />
      </div>
   </div>
</template>

<script>
import SimpleDropdown from './SimpleDropdown';
import SideBySide from './SideBySide';
import StylishDropdown from './StylishDropdown';

export default {
   name: 'NavBarItems',
   components: {
      SimpleDropdown,
      SideBySide,
      StylishDropdown,
   },

   props: {
      itemName: {
         type: String,
         required: true,
      },
      sidebysideDropdown: {
         type: Boolean,
         default: false,
      },
      dropdownItems: {
         type: Array,
         default: () => [],
      },

      stylishDropdown: {
         type: Boolean,
         default: false,
      },
      simpleDropdown: {
         type: Boolean,
         default: false,
      },
      itemLink: {
         type: String,
         default: '',
      },
   },
   data() {
      return {
         // A click on the *dropdown panel* (not this whole item - see the
         // @click on .drop-down, not on the root element) sets this
         // immediately and synchronously, right as the click happens -
         // deliberately not driven by the resulting route change, which
         // only lands later and asynchronously (Vue Router navigation
         // isn't synchronous), by which point the cursor's real :hover
         // state has already drifted in ways that made that approach
         // flaky in practice. Scoped to just the panel, not the trigger
         // link too, because on a touch device (or one emulated as one)
         // tapping the trigger to *open* the dropdown in the first place
         // is itself a click - suppressing on that click as well meant
         // the dropdown could never actually be seen there, since
         // there's no real mouseleave on touch to ever clear it again.
         // Only lets go on this item's OWN mouseleave - not on any
         // mousemove. The dropdown panel covers most of the viewport, so
         // "the next mousemove" fires from the tiniest cursor jitter
         // while the mouse is still structurally inside it, which would
         // un-suppress the CSS :hover rule while it's still matching and
         // flash the menu straight back open. mouseleave only fires once
         // the mouse has actually left this item, by which point :hover
         // itself has already stopped matching, so there's nothing left
         // to flash. Scoped per-item this way (rather than one flag
         // PrimaryNavigation.vue broadcasts to every dropdown on every
         // route change) so items the visitor never actually touched
         // don't get suppressed too - that broke their first-ever hover,
         // since they'd have no prior mouseleave to clear it.
         localSuppressHover: false,
      };
   },
   computed: {
      isDropdown() {
         return this.simpleDropdown || this.sidebysideDropdown || this.stylishDropdown;
      },
   },
   methods: {
      goTo(url) {
         if (url) {
            this.$router.push(url).catch(() => {});
         }
      },
      onClick(event) {
         // Always handled here, never left to the native <a> (which is
         // target="_top" for historical reasons, so its own default
         // behaviour would always open same-tab - wrong for a real
         // external link like Pricing -> cart.fusioncharts.com). External
         // links, and /dev and /blog (root-relative, but served by a
         // different backend behind the same host), go through
         // $openReload so external domains actually get a new tab.
         // Everything else - internal links, and dropdown triggers with
         // no itemLink at all - goes through the router (a no-op for the
         // latter, since goTo ignores an empty url) so clicking never
         // hard-reloads the page.
         event.preventDefault();
         const needsRealNavigation = this.$reloadType(this.itemLink);
         if (needsRealNavigation) {
            this.$openReload(this.itemLink);
         } else {
            this.goTo(this.itemLink);
         }
      },
   },
};
</script>

<style lang="scss" scoped>
.nav-item {
   list-style-type: none;
   // flex + stretch (not inline-block) so this fills the now full-height
   // parent <li> instead of shrink-wrapping to the link's own text height -
   // see the align-self:stretch comment on .navigation__items-item in
   // PrimaryNavigation.vue for why that matters for hover.
   display: flex;
   align-items: center;
   align-self: stretch;
   margin-left: 2px;
   // Not position:relative - the mega-menu panel positions itself relative
   // to .primary-navigation-container__inner instead (see app.scss), so
   // every dropdown opens flush with the nav's own left content edge
   // rather than under its own trigger. Matches the reference exactly:
   // its dropdowns all share one fixed horizontal position too, which is
   // also what keeps later items (Resources, Documentation) from having
   // a wide panel overflow off the right edge of the viewport.
   transition: all ease 0.35s;

   &:hover .nav-item__link {
      color: #5b2fb0;
   }
   &:hover .dropdown-icon {
      transform: rotate(180deg);
   }
   &:hover .drop-down {
      opacity: 1;
      visibility: visible;
   }
   &__link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      text-decoration: none;
      color: #0b0b28;
      // basefontBold (not basefontSemiBold) - the reference's real bold weight.
      // basefontSemiBold's @font-face is only registered at weight 600, so
      // requesting weight 700 on it forced the browser to synthesize a fake
      // bold, which both read visibly thinner than the reference AND threw
      // off the chevron's flex-centered vertical position enough to need a
      // manual nudge. The real bold face needs no such correction.
      font-family: basefontBold, 'Helvetica Neue', Arial, sans-serif;
      font-weight: 700;
      transition: color 0.13s ease;
      font-size: 15px;
      padding: 8px 13px;
      border-radius: 8px;
      .dropdown-icon {
         width: 9px;
         height: 9px;
         opacity: 0.55;
         transition: transform 0.18s ease;
      }
   }
   .drop-down {
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.16s ease, visibility 0.16s ease;
   }
   // Forces the dropdown shut right after an SPA route change, overriding
   // the :hover rule above - see suppressDropdownHover in
   // PrimaryNavigation.vue for why plain :hover isn't enough post-navigate.
   &.suppress-hover:hover .drop-down {
      visibility: hidden;
      opacity: 0;
   }
}
</style>
